import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { Stack } from '@mui/system';
import { TabList, TabPanel, TabContext } from '@mui/lab';
import { Box, Tab, Grid, Backdrop, IconButton } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import { fetchAllGroups } from 'src/services/group.users.service';
import { getDepartmentById } from 'src/services/department.service';
import {
  deleteUserPermission,
  fetchUserPermissions,
  fetchUsersWithPermissions,
  addMultipleUsersPermissions,
} from 'src/services/ticket.permissions.service';

import Loader from 'src/components/loaders/Loader';
import Iconify from 'src/components/iconify/Iconify';

import TicketDelegation from 'src/sections/ticketDetails/subSections/TicketDelegation';

import InfoTab from './subSections/InfoTab';
import { decrypt } from '../../utils/hashCode';
import { useToast } from '../../hooks/useToast';
import Permissions from './subSections/Permissions';
import DetailsContent from './subSections/DetailsContent';
import { fetchStatuses } from '../../services/status.service';
import TicketConversation from './ticketConversation/TicketConversation';
import { gridStyle, iconifyStyle, secondBoxtStyle, secondGridStyle } from './styles';
import {
  fetchUsers,
  getUserById,
  getUsersByDepartment,
} from '../../services/user.service';
import {
  updateTicket,
  getTicketById,
  assignTicketManually,
} from '../../services/ticket.service';

export default function TicketDetailsView() {
  const {
    id: currentUserId,
    role: currentUserRole,
    departmentId: currentDepartmentId,
  } = useAuth();

  const [departmentData, setDepartmentData] = useState({
    id: 0,
    name: '',
    description: '',
    image: '',
  });
  const [ticketData, setTicketData] = useState();
  const [statusesList, setStatusesList] = useState([]);
  const [currentStatus, setCurrentStatus] = useState({});
  const [departmentUsers, setDepartmentUsers] = useState([]);
  const [tabValue, setTabValue] = useState('1');
  const [isLoading, setIsloading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [commentsData, setCommentsData] = useState([]);
  const [customerData, setCustomerData] = useState({
    id: 0,
    username: '',
    email: '',
  });
  const [agentData, setAgentData] = useState({
    id: 0,
    username: '',
    email: '',
    departmentId: 0,
  });
  const [users, setUsers] = useState([]);
  const [groupUsers, setGroupUsers] = useState([]);

  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { ticketId } = useParams();
  const [decryptedTicketId, setDecryptedTicketId] = useState();
  const [hasWritePermission, setHasWritePermission] = useState(false);
  const [hasReadPermission, setHasReadPermission] = useState(false);
  const [permissionUsers, setPermissionUsers] = useState([]);

  const navigate = useNavigate();
  const { showToast, hideToast } = useToast();

  const fetchTicketData = async (id) => {
    setIsloading(true);
    genericApiToaster(
      getTicketById,
      id,
      '',
      t('errorMessages.couldntLoadTicket'),
      showToast,
      hideToast,
      t,
      setTicketData,
      () => navigate('/404')
    );
  };

  const checkHasPermission = async (ticketId) => {
    genericApiToaster(
      fetchUserPermissions,
      [ticketId, currentUserId],
      '',
      t('errorMessages.couldntLoadUserPermissions'),
      showToast,
      hideToast,
      t,
      (permissions) => {
        if (!permissions || !permissions[0]) {
          navigate('/404');
        }
      },
      () => navigate('/404')
    );
  };

  useEffect(() => {
    if (ticketId) {
      const decryptedId = decrypt(ticketId);
      setDecryptedTicketId(decryptedId);
      fetchTicketData(decryptedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async (userId, setState) => {
    if (userId !== -1) {
      genericApiToaster(
        getUserById,
        userId,
        '',
        t('errorMessages.couldntLoadUser'),
        showToast,
        hideToast,
        t,
        setState
      );
    }
  };

  const fetchDepartment = async (depId) => {
    genericApiToaster(
      getDepartmentById,
      depId,
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      setDepartmentData
    );
  };

  const assignTicket = async (userId, statusId) => {
    genericApiToaster(
      assignTicketManually,
      [parseInt(decryptedTicketId, 10), userId, statusId],
      t('successMessages.successTicketAssign'),
      t('errorMessages.errorUpdatingTicket'),
      showToast,
      hideToast,
      t,
      () => {
        fetchTicketData(decryptedTicketId);
      }
    );
  };

  const delegateTicket = (delegatedTicketData) => {
    genericApiToaster(
      updateTicket,
      delegatedTicketData,
      currentUserRole.authority === 'ROLE_AGENT' &&
        ticketData?.assignedTo === currentUserId
        ? t('successMessages.successDelegateTicket')
        : t('successMessages.successReassignTicket'),
      currentUserRole.authority === 'ROLE_AGENT' &&
        ticketData?.assignedTo === currentUserId
        ? t('errorMessages.couldntDelegateTicket')
        : t('errorMessages.couldntReassignTicket'),
      showToast,
      hideToast,
      t,
      () => {
        fetchTicketData(decryptedTicketId);
      }
    );
  };

  const getStatuses = async (depId) => {
    genericApiToaster(
      fetchStatuses,
      [],
      '',
      t('errorMessages.couldntLadStatuses'),
      showToast,
      hideToast,
      t,
      (res) => {
        const cleanStatuses = res.filter(
          (status) =>
            status.name !== 'Hidden' &&
            (status.departmentId === depId || status.departmentId === 0)
        );
        setStatusesList(cleanStatuses);
      }
    );
  };

  const fetchDepAgents = async () => {
    genericApiToaster(
      getUsersByDepartment,
      ticketData.departmentId,
      '',
      t('errorMessages.couldntLoadDepUsers'),
      showToast,
      hideToast,
      t,
      (res) =>
        setDepartmentUsers(
          res.filter((user) => user.appUserRole === 'ROLE_AGENT' && !user.archived)
        )
    );
  };

  const fetchAllUsers = async () => {
    genericApiToaster(
      fetchUsers,
      [],
      '',
      t('errorMessages.couldntLoadDepUsers'),
      showToast,
      hideToast,
      t,
      (res) => setUsers(res.filter((user) => !user.archived))
    );
  };
  const { id } = useAuth();

  const getGroupUsers = async () => {
    genericApiToaster(
      fetchAllGroups,
      id,
      '',
      t('errorMessages.errorFetchGroupUsers'),
      showToast,
      hideToast,
      t,
      setGroupUsers
    );
  };

  useEffect(() => {
    fetchAllUsers();
    getGroupUsers();
    if (
      ticketData &&
      currentStatus &&
      currentStatus.systemMappedStatus !== 'UNASSIGNED' &&
      agentData.departmentId !== 0
    )
      fetchDepartment(agentData.departmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentData]);

  const getCurrentStatus = (idStatus) => {
    setCurrentStatus(statusesList.find((status) => status.statusId === idStatus));
  };

  useEffect(() => {
    if (ticketData) {
      if (currentStatus && currentStatus.systemMappedStatus !== 'UNASSIGNED') {
        fetchUser(ticketData.assignedTo, setAgentData);
      }
      fetchUser(ticketData.createdBy, setCustomerData);
      fetchDepAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketData, currentStatus]);

  useEffect(() => {
    if (ticketData) {
      getCurrentStatus(ticketData.status_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusesList, ticketData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchPermissions = async () => {
    if (!ticketData) return;
    if (
      currentUserRole.authority === 'ROLE_ADMIN' ||
      ticketData.createdBy === currentUserId ||
      ticketData.assignedTo === currentUserId ||
      currentDepartmentId === ticketData.departmentId
    ) {
      setHasReadPermission(true);
      setHasWritePermission(true);
      return;
    }
    genericApiToaster(
      fetchUserPermissions,
      [decryptedTicketId, currentUserId],
      '',
      t('errorMessages.couldntLoadUserPermissions'),
      showToast,
      hideToast,
      t,
      (response) => {
        setHasReadPermission(response[0]);
        setHasWritePermission(response[1]);
        checkHasPermission(decryptedTicketId);
      }
    );
  };

  const getUsersWithPermissions = async () => {
    setPermissionsLoading(true);
    genericApiToaster(
      fetchUsersWithPermissions,
      decryptedTicketId,
      '',
      t('errorMessages.couldntLoadUser'),
      showToast,
      hideToast,
      t,
      setPermissionUsers,
      undefined,
      () => setPermissionsLoading(false)
    );
  };

  const deletePermission = async (ticketId, userId, errorCallback) => {
    genericApiToaster(
      deleteUserPermission,
      { ticketId, userId },
      t('successMessages.successUserPermissiondelete'),
      t('errorMessages.errorDeletingUserPermission'),
      showToast,
      hideToast,
      t,
      () => setPermissionUsers([...permissionUsers].filter((user) => user.id !== userId)),
      errorCallback
    );
  };

  const addMultiplePermissions = async (
    ticketId,
    userIds,
    isReadPermission,
    isWritePermission,
    successCallback
  ) => {
    if (isWritePermission) {
      isReadPermission = true;
    }
    genericApiToaster(
      addMultipleUsersPermissions,
      { ticketId, userIds, isReadPermission, isWritePermission },
      t('successMessages.successAddingPermission'),
      t('errorMessages.errorAddingPermission'),
      showToast,
      hideToast,
      t,
      successCallback
    );
  };

  useEffect(() => {
    getStatuses(ticketData?.departmentId);
    fetchPermissions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketData]);

  useEffect(() => {
    if (decryptedTicketId) {
      getUsersWithPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedTicketId]);

  return (
    <>
      <Backdrop
        sx={{
          bgcolor: 'rgb(121 121 121 / 32%)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Loader size={175} hideText />
        </Box>
      </Backdrop>
      <Grid columns={5} sx={gridStyle} container spacing={1}>
        <Grid item xs={5} lg={2} md={2} sm={5} bgcolor="#F5F5F5">
          <TabContext value={tabValue}>
            <Box
              sx={{
                marginLeft: '30px',
                marginRight: isSmallScreen ? '10px' : '30px',
                borderBottom: 1,
                borderColor: 'divider',
              }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <TabList
                onChange={handleTabChange}
                aria-label="ticket details tabs "
                sx={{ width: '100%' }}
              >
                <Tab
                  sx={{ width: '33%' }}
                  icon={
                    <Iconify width={isSmallScreen ? 20 : 30} icon="akar-icons:info" />
                  }
                  style={{ color: '#121212' }}
                  iconPosition="bottom"
                  value="1"
                />

                <Tab
                  sx={{ width: '33%' }}
                  icon={
                    <Iconify width={isSmallScreen ? 22 : 30} icon="ic:baseline-history" />
                  }
                  style={{
                    color: '#121212',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  value="2"
                />
                <Tab
                  sx={{ width: '33%' }}
                  icon={
                    <Iconify width={isSmallScreen ? 20 : 30} icon="tabler:lock-access" />
                  }
                  style={{ color: '#121212' }}
                  iconPosition="bottom"
                  value="3"
                />
              </TabList>
              {isSmallScreen && (
                <Box sx={secondBoxtStyle}>
                  <Stack direction="row" justifyContent="end" alignItems="end">
                    <IconButton
                      onClick={() => {
                        if (window.opener) {
                          window.close();
                        } else {
                          navigate(-1);
                        }
                      }}
                    >
                      <Iconify
                        sx={{ ...iconifyStyle, width: 25 }}
                        icon="eva:close-square-fill"
                      />
                    </IconButton>
                  </Stack>
                </Box>
              )}
            </Box>

            <TabPanel value="1" xs={5} sm={5} md={2} sx={{ padding: '0px 24px' }}>
              <DetailsContent
                hasReadPermission={hasReadPermission}
                hasWritePermission={hasWritePermission}
                row={ticketData}
                status={currentStatus}
                statusesList={statusesList}
                departmentUsers={departmentUsers}
                fetchTicketData={fetchTicketData}
              />
              <TicketDelegation
                currentStatus={currentStatus}
                ticketData={ticketData}
                departmentUsers={departmentUsers}
                assignTicket={assignTicket}
                ticketId={decryptedTicketId}
                statusesList={statusesList}
                delegateTicket={delegateTicket}
              />
            </TabPanel>
            <TabPanel value="2" sx={{ padding: '0px 24px' }}>
              <InfoTab
                fetchUser={fetchUser}
                currentStatus={currentStatus}
                ticketData={ticketData}
                assignTicket={assignTicket}
                ticketId={decryptedTicketId}
                statusesList={statusesList}
                departmentUsers={departmentUsers}
                agentData={agentData}
                customerData={customerData}
                departmentData={departmentData}
                commentsData={commentsData}
                hasReadPermission={hasReadPermission}
                hasWritePermission={hasWritePermission}
              />
            </TabPanel>
            <TabPanel value="3" sx={{ padding: '0px 24px' }}>
              <Permissions
                hasReadPermission={hasReadPermission}
                hasWritePermission={hasWritePermission}
                ticketId={decryptedTicketId}
                usersList={users}
                ticketData={ticketData}
                groupList={groupUsers}
                users={permissionUsers}
                fetchTicketData={fetchTicketData}
                setPermissionUsers={setPermissionUsers}
                deletePermission={deletePermission}
                permissionsLoading={permissionsLoading}
                addMultiplePermissions={addMultiplePermissions}
              />
            </TabPanel>
          </TabContext>
        </Grid>

        <Grid item xs={5} lg={3} md={3} sm={5} sx={secondGridStyle}>
          <TicketConversation
            statusesList={statusesList}
            fetchUser={fetchUser}
            row={ticketData}
            customerAvatar={customerData?.avatar}
            agentAvatar={agentData?.avatar}
            currentStatus={currentStatus}
            setIsLoading={setIsloading}
            commentsData={commentsData}
            setCommentsData={setCommentsData}
            ticketId={decryptedTicketId}
            departmentUsers={departmentUsers}
            hasReadPermission={hasReadPermission}
            hasWritePermission={hasWritePermission}
          />
        </Grid>
      </Grid>
    </>
  );
}
